import React, { useEffect, useMemo, useRef, useState } from "react";
import { mixUrl, takeChannel } from "@/lib/audioPool";
import { AnimatePresence, motion } from "framer-motion";
import { Pencil, RotateCcw, SkipForward, X } from "lucide-react";
import NexWordmark from "@/components/NexWordmark";
import IntroLogo from "../intro/IntroLogo";
import { IntroGrid, IntroParticles, IntroSweep } from "../intro/IntroFx";
import BookCallForm from "../tour/BookCallForm";
import TourLineEditor from "../tour/TourLineEditor";
import { useGuidedTour } from "@/lib/guidedTour";
import { possessive } from "@/lib/demoBrand";
import { PhaseTrackScene, SessionScene, SavingsScene } from "./OutroScenes";

const SCENE_MS = { phases: 2900, session: 2600, savings: 2900 };
const NARRATION_DELAY = 400;

export default function CinematicOutro({ name, logoUrl, accent, phases, session, savings, audioUrl, musicUrl, line, isAdmin, onLineSaved, onReplayTour, onClose }) {
  const scenes = useMemo(() => {
    const list = ["phases"];
    if (session.leads || session.approved || session.recovered) list.push("session");
    if (savings.annual) list.push("savings");
    return [...list, "final"];
  }, [session, savings.annual]);
  const { start } = useGuidedTour();
  const [idx, setIdx] = useState(0);
  const [editing, setEditing] = useState(false);
  const audioRef = useRef([]);
  const scene = scenes[idx];

  useEffect(() => {
    const timers = [];
    let t = 0;
    scenes.slice(0, -1).forEach((s, i) => { t += SCENE_MS[s]; timers.push(setTimeout(() => setIdx(i + 1), t)); });
    const mh = musicUrl ? takeChannel("music", mixUrl(musicUrl, 0.5)) : null;
    const ah = audioUrl ? takeChannel("voice", audioUrl) : null;
    audioRef.current = [mh?.el, ah?.el];
    mh?.play().catch(() => {});
    if (ah) timers.push(setTimeout(() => ah.play().catch(() => {}), NARRATION_DELAY));
    return () => {
      timers.forEach(clearTimeout);
      [mh, ah].forEach((h) => h?.release());
    };
  }, [scenes, audioUrl, musicUrl]);

  const halt = () => audioRef.current.forEach((el) => { try { el?.pause(); } catch {} });
  const skip = () => setIdx(scenes.length - 1);

  return (
    <motion.div
      data-testid="veroogle-outro"
      className="fixed inset-0 z-[120] overflow-y-auto overflow-x-hidden bg-[#03060F] text-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <IntroGrid />
      <IntroSweep key={`sweep-${scene}`} />
      {scene === "savings" && <IntroParticles count={48} />}
      {scene === "final" && <IntroParticles key="final-burst" count={42} />}

      <div className="relative z-10 min-h-full w-full flex flex-col items-center justify-center px-6 py-20">
        <AnimatePresence mode="wait">
          {scene === "phases" && <PhaseTrackScene key="phases" phases={phases} accent={accent} />}
          {scene === "session" && <SessionScene key="session" session={session} accent={accent} />}
          {scene === "savings" && <SavingsScene key="savings" annual={savings.annual} name={name} accent={accent} />}
          {scene === "final" && (
            <FinalScene key="final" name={name} logoUrl={logoUrl} accent={accent} savings={savings}
              onReplayTour={() => { halt(); onReplayTour(); start("auto"); }} />
          )}
        </AnimatePresence>
        {editing && (
          <div className="mt-8 w-full max-w-xl rounded-2xl border border-white/10 bg-[#0C1322]/95 p-4" data-testid="veroogle-outro-line-editor">
            <TourLineEditor step="OUT" text={line?.text || ""} edited={line?.edited}
              onCancel={() => setEditing(false)}
              onSaved={(l) => { onLineSaved?.(l); setEditing(false); }} />
          </div>
        )}
      </div>

      <div className="fixed top-5 right-5 z-20 flex items-center gap-2">
        {isAdmin && line && !editing && (
          <button onClick={() => { halt(); setEditing(true); }} data-testid="veroogle-outro-edit" aria-label="Edit closing narration"
            className="p-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-cyan-300 hover:border-cyan-400/50 transition-colors">
            <Pencil className="w-4 h-4" />
          </button>
        )}
        {scene !== "final" && (
          <button onClick={skip} data-testid="veroogle-outro-skip"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-white/30 font-mono text-[10px] tracking-[0.16em] uppercase transition-colors">
            Skip <SkipForward className="w-3.5 h-3.5" />
          </button>
        )}
        <button onClick={() => { halt(); onClose(); }} data-testid="veroogle-outro-close" aria-label="Close finale"
          className="p-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-white/30 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}

function FinalScene({ name, logoUrl, accent, savings, onReplayTour }) {
  return (
    <motion.div
      data-testid="veroogle-outro-final"
      className="w-full max-w-2xl flex flex-col items-center text-center"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <IntroLogo name={name} logoUrl={logoUrl} />
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
        className="mt-4 flex items-center gap-3" data-testid="veroogle-outro-powered">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-400">Engineered by</span>
        <NexWordmark size="text-2xl" />
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-400">engines</span>
      </motion.div>
      <motion.h2
        data-testid="veroogle-outro-headline"
        initial={{ opacity: 0, y: 16, filter: "blur(8px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ delay: 1.1, duration: 0.6 }}
        className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]"
      >
        Let's build <span className="bg-gradient-to-r from-white to-emerald-300 bg-clip-text text-transparent">{possessive(name)}</span> engine.
      </motion.h2>
      {savings.annual > 0 && (
        <motion.div
          data-testid="veroogle-outro-savings-chip"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
          className="mt-5 inline-flex items-center gap-2 px-4 py-1.5 rounded-full border font-mono text-[11px] tracking-[0.18em] uppercase text-emerald-200"
          style={{ borderColor: `${accent}66`, background: `${accent}14` }}
        >
          ${savings.annual.toLocaleString()} / year back in your pocket
        </motion.div>
      )}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.8 }}
        className="mt-6 w-full max-w-lg text-left">
        <BookCallForm />
      </motion.div>
      <motion.button
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.1 }}
        onClick={onReplayTour}
        data-testid="veroogle-outro-replay-tour"
        className="mt-6 inline-flex items-center gap-1.5 text-slate-400 hover:text-white font-mono text-[10px] tracking-[0.18em] uppercase transition-colors"
      >
        <RotateCcw className="w-3.5 h-3.5" /> Replay the tour
      </motion.button>
    </motion.div>
  );
}
